import { Link } from 'react-router';
import type { ReactNode } from 'react';

/* --------------------------------------------------------------------------
 *  Black Gold Archive · homepage
 *  ------------------------------------------------------------------------
 *  This page replaces the previous "four cards on a black background" entry
 *  with the Black Gold Archive facade: a magazine-spread composition of
 *  five sections (Topbar · Hero · Atelier · Catalogue · Ledger · Footer).
 *
 *  Composition rule: every section is a direct child of `.axi-archive` so
 *  the page composes like a magazine spread, not a dashboard. The hero
 *  uses a per-character stagger reveal (CSS-only) and a delayed gold
 *  underline on the italic display mark. All motion is disabled under
 *  `prefers-reduced-motion: reduce`.
 *
 *  Data flow: this page is fully props-driven. `atelier` and `ledger`
 *  are computed by `App.tsx` from `import.meta.glob` and CHANGE.md and
 *  passed in at mount. Default fallbacks are provided here so the page
 *  is still meaningful when rendered without injection (tests, demos).
 *  -----------------------------------------------------------------------*/

export type CatalogueEntry = {
  href: string;
  title: string;
  desc: ReactNode;
  meta?: string;
};

export type LedgerEntry = {
  date: string;
  title: string;
  hash?: string;
};

export type AtelierStats = {
  components: number;
  themes: number;
  routes: number;
};

export type IndexPageProps = {
  /** Active theme slug; mirrors the page onto `<html data-axi-theme>`. */
  themeSlug?: string;
  /** Live counts fed by App from `import.meta.glob` + `themeRegistry.list()`. */
  atelier?: AtelierStats;
  /** Top-3 entries from CHANGE.md; pre-parsed so tests do not need fs. */
  ledger?: LedgerEntry[];
  /** Catalogue entries; defaults to the four /examples/* routes. */
  catalogue?: CatalogueEntry[];
};

const DEFAULT_CATALOGUE: CatalogueEntry[] = [
  {
    href: '/examples/tokens',
    title: 'Tokens · 令牌',
    desc: (
      <>
        由 <code>@axi/tokens</code> 注入 <code>&lt;style data-axi-tokens&gt;</code> 的全部 CSS 变量。键名、值、颜色 swatch 一一对应。
      </>
    ),
    meta: 'CSS variables'
  },
  {
    href: '/examples/themes',
    title: 'Themes · 主题',
    desc: (
      <>
        插件式主题注册中心。当前仅 <code>dark-gold</code> 一例；新增主题只需在 <code>src/themes/</code> 下加一个文件并由 barrel 导入。
      </>
    ),
    meta: 'Theme plugins'
  },
  {
    href: '/examples/components',
    title: 'Components · 组件',
    desc: (
      <>
        所有 props-driven 原始组件的样本，使用占位 props 渲染，可与主题切换联动以观察视觉变量如何作用。
      </>
    ),
    meta: 'Props-driven'
  },
  {
    href: '/examples/dashboard',
    title: 'Dashboard · EXCEL HR 示例',
    desc: (
      <>
        一个完整的 <code>&lt;DashboardLayout&gt;</code> 示例页面。它是组件库的「成品」展示，不是本仓库的定位本身。
      </>
    ),
    meta: 'Layout sample'
  }
];

const DEFAULT_LEDGER: LedgerEntry[] = [
  { date: '2026-07-10', title: 'Zero-context handoff manifest (v2) + VERIFICATION.md', hash: 'af86735' },
  { date: '2026-07-12', title: 'Initialize 8 business pages backed by mock data', hash: 'fbee444' },
  { date: '2026-07-12', title: 'Realign positioning: / becomes the design-system gallery', hash: 'dbbdfc3' }
];

const DEFAULT_ATELIER: AtelierStats = { components: 0, themes: 0, routes: 0 };

/**
 * Split a string into individual `<span>`s for the stagger-reveal
 * animation. We split by Unicode code-point so CJK characters and
 * combined grapheme clusters stay intact (no broken compound glyphs).
 */
function splitForStagger(text: string): Array<{ char: string; delay: number }> {
  const out: Array<{ char: string; delay: number }> = [];
  let i = 0;
  // Array.from on a string yields code-point-correct segments for BMP and
  // surrogate pairs (CJK, emoji ZWJ sequences handled by the JS engine).
  for (const ch of Array.from(text)) {
    out.push({ char: ch, delay: i * 35 });
    i += 1;
  }
  return out;
}

function BrandMark() {
  return (
    <span className="axi-archive__brand-mark" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12,3 21,12 12,21 3,12" />
      </svg>
    </span>
  );
}

type HeroProps = {
  themeSlug?: string;
};

function Hero({ themeSlug }: HeroProps) {
  const titleChars = splitForStagger('AxiomaticWorld');
  // 'Design Archive' rendered as plain text + a marked span for the gold
  // underline animation; splitting it would defeat the underline.
  return (
    <section className="axi-archive__hero" data-axi-theme={themeSlug}>
      <div>
        <p className="axi-archive__eyebrow">Archive / 001</p>
        <h1 className="axi-archive__title">
          {titleChars.map(({ char, delay }, i) => (
            <span
              key={`hero-${i}`}
              className="axi-archive__title-char"
              style={{ animationDelay: `${delay}ms` }}
            >
              {char}
            </span>
          ))}
          <br />
          <span className="axi-archive__title-mark">Design Archive</span>
        </h1>
        <p className="axi-archive__lead">
          这是一份可寻址的设计令牌、主题与组件预览。每一项都来自 <code>@axi/tokens</code> 与当前主题插件，可与 <code>src/components/*</code> 中的 props-driven 原始组件联动。
        </p>
        <p className="axi-archive__quote">
          The archive is not a marketing page. It is the front door of a working design system.
        </p>
      </div>

      <aside className="axi-archive__meta" aria-label="archive metadata">
        <div className="axi-archive__meta-row">
          <span className="axi-archive__meta-label">Theme</span>
          <span className="axi-archive__meta-value">{themeSlug ?? 'dark-gold'}</span>
        </div>
        <div className="axi-archive__meta-row">
          <span className="axi-archive__meta-label">Build</span>
          <span className="axi-archive__meta-value">v0.1.0 / dev</span>
        </div>
        <div className="axi-archive__meta-row">
          <span className="axi-archive__meta-label">Last commit</span>
          <span className="axi-archive__meta-value">af86735</span>
        </div>
        <div className="axi-archive__meta-row">
          <span className="axi-archive__meta-label">Sources</span>
          <span className="axi-archive__meta-value">@axi/tokens + components/* + themes/*</span>
        </div>
      </aside>
    </section>
  );
}

type AtelierStatsProps = {
  atelier: AtelierStats;
};

function AtelierStatsRow({ atelier }: AtelierStatsProps) {
  const items = [
    { figure: atelier.components, caption: 'Components' },
    { figure: atelier.themes, caption: 'Themes' },
    { figure: atelier.routes, caption: 'Routes' }
  ];
  return (
    <section className="axi-archive__atelier" aria-label="atelier counts">
      {items.map(({ figure, caption }) => (
        <div className="axi-archive__atelier-item" key={caption}>
          <span className="axi-archive__atelier-figure">
            {String(figure).padStart(2, '0')}
          </span>
          <span className="axi-archive__atelier-caption">{caption}</span>
        </div>
      ))}
    </section>
  );
}

type CatalogueCardProps = {
  index: number;
  total: number;
  entry: CatalogueEntry;
};

function CatalogueCard({ index, total, entry }: CatalogueCardProps) {
  const current = String(index + 1).padStart(2, '0');
  const max = String(total).padStart(2, '0');
  return (
    <Link className="axi-archive__card" to={entry.href}>
      <span className="axi-archive__card-num" aria-hidden="true">
        <span className="axi-archive__card-num-cur">{current}</span> / {max}
      </span>
      <h2 className="axi-archive__card-title">{entry.title}</h2>
      <p className="axi-archive__card-desc">{entry.desc}</p>
      <div className="axi-archive__card-foot">
        <span className="axi-archive__card-href">{entry.href}</span>
        {entry.meta ? <span>{entry.meta}</span> : null}
      </div>
    </Link>
  );
}

type LedgerTimelineProps = {
  entries: LedgerEntry[];
};

function LedgerTimeline({ entries }: LedgerTimelineProps) {
  return (
    <div className="axi-archive__ledger">
      {entries.map((entry) => (
        <article className="axi-archive__ledger-row" key={`${entry.date}-${entry.hash ?? entry.title}`}>
          <span className="axi-archive__ledger-date">{entry.date}</span>
          <span className="axi-archive__ledger-hash">{entry.hash ?? '——'}</span>
          <h3 className="axi-archive__ledger-title">{entry.title}</h3>
        </article>
      ))}
    </div>
  );
}

function IndexPage({
  themeSlug,
  atelier = DEFAULT_ATELIER,
  ledger = DEFAULT_LEDGER,
  catalogue = DEFAULT_CATALOGUE
}: IndexPageProps = {}) {
  return (
    <div className="axi-archive" data-axi-theme={themeSlug}>
      <header className="axi-archive__topbar">
        <span className="axi-archive__brand">
          <BrandMark />
          Axi Design
        </span>
        <span className="axi-archive__topbar-meta">Index · v0.1.0</span>
      </header>

      <Hero themeSlug={themeSlug} />

      <AtelierStatsRow atelier={atelier} />

      <section className="axi-archive__section" aria-labelledby="catalogue-title">
        <header className="axi-archive__section-head">
          <span className="axi-archive__section-num">02 · Catalogue</span>
          <h2 id="catalogue-title" className="axi-archive__section-title">目录</h2>
          <span className="axi-archive__section-rule" aria-hidden="true" />
        </header>
        <div className="axi-archive__catalogue">
          {catalogue.map((entry, i) => (
            <CatalogueCard key={entry.href} index={i} total={catalogue.length} entry={entry} />
          ))}
        </div>
      </section>

      <section className="axi-archive__section" aria-labelledby="ledger-title">
        <header className="axi-archive__section-head">
          <span className="axi-archive__section-num">03 · Ledger</span>
          <h2 id="ledger-title" className="axi-archive__section-title">最近更新</h2>
          <span className="axi-archive__section-rule" aria-hidden="true" />
        </header>
        <LedgerTimeline entries={ledger} />
      </section>

      <footer className="axi-archive__footer">
        <span>© AxiomaticWorld · 设计系统门面</span>
        <span className="axi-archive__footer-tag">Not a marketing page</span>
      </footer>
    </div>
  );
}

export default IndexPage;